import api from './api';
import { ApiResponse } from '../types';
import {
  AgingResponse,
  CollectionRateResponse,
  ByCategoryResponse,
  ForecastResponse,
  CollectionTrendResponse,
  TopOutstandingStudentsResponse,
  PaymentMethodMixResponse,
  AdjustmentsSummaryResponse,
} from '../types/fees';

/**
 * Thin service for /api/finance-reports/fees/*.
 * Every call returns a shape ready for the FeeReportsPage — no client-side
 * aggregation.
 */

export interface AgingFilter {
  asOfDate?: string;                                       // ISO
  breakBy?: 'class' | 'section' | 'category';
  sessionId?: string;
  classId?: string;
}

export const getAging = async (filter: AgingFilter = {}): Promise<AgingResponse> => {
  const sp = new URLSearchParams();
  if (filter.asOfDate)  sp.append('asOfDate', filter.asOfDate);
  if (filter.breakBy)   sp.append('breakBy', filter.breakBy);
  if (filter.sessionId) sp.append('sessionId', filter.sessionId);
  if (filter.classId)   sp.append('classId', filter.classId);
  const response = await api.get<ApiResponse<AgingResponse>>(`/finance-reports/fees/aging?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export interface CollectionRateFilter {
  dateFrom?: string;
  dateTo?: string;
  groupBy?: 'class' | 'category';
  sessionId?: string;
  classId?: string;
}

export const getCollectionRate = async (filter: CollectionRateFilter = {}): Promise<CollectionRateResponse> => {
  const sp = new URLSearchParams();
  if (filter.dateFrom)  sp.append('dateFrom', filter.dateFrom);
  if (filter.dateTo)    sp.append('dateTo', filter.dateTo);
  if (filter.groupBy)   sp.append('groupBy', filter.groupBy);
  if (filter.sessionId) sp.append('sessionId', filter.sessionId);
  if (filter.classId)   sp.append('classId', filter.classId);
  const response = await api.get<ApiResponse<CollectionRateResponse>>(`/finance-reports/fees/collection-rate?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export interface ByCategoryFilter {
  sessionId?: string;
  classId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export const getByCategory = async (filter: ByCategoryFilter = {}): Promise<ByCategoryResponse> => {
  const sp = new URLSearchParams();
  if (filter.sessionId) sp.append('sessionId', filter.sessionId);
  if (filter.classId)   sp.append('classId', filter.classId);
  if (filter.dateFrom)  sp.append('dateFrom', filter.dateFrom);
  if (filter.dateTo)    sp.append('dateTo', filter.dateTo);
  const response = await api.get<ApiResponse<ByCategoryResponse>>(`/finance-reports/fees/by-category?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export interface ForecastFilter {
  horizonDays?: number;
  sessionId?: string;
  classId?: string;
}

export const getDuesForecast = async (filter: ForecastFilter = {}): Promise<ForecastResponse> => {
  const sp = new URLSearchParams();
  if (filter.horizonDays) sp.append('horizonDays', String(filter.horizonDays));
  if (filter.sessionId)   sp.append('sessionId', filter.sessionId);
  if (filter.classId)     sp.append('classId', filter.classId);
  const response = await api.get<ApiResponse<ForecastResponse>>(`/finance-reports/fees/dues-forecast?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export interface CollectionTrendFilter {
  months?: number;
  sessionId?: string;
  classId?: string;
}

export const getCollectionTrend = async (filter: CollectionTrendFilter = {}): Promise<CollectionTrendResponse> => {
  const sp = new URLSearchParams();
  if (filter.months)    sp.append('months', String(filter.months));
  if (filter.sessionId) sp.append('sessionId', filter.sessionId);
  if (filter.classId)   sp.append('classId', filter.classId);
  const response = await api.get<ApiResponse<CollectionTrendResponse>>(`/finance-reports/fees/collection-trend?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export interface TopOutstandingStudentsFilter {
  limit?: number;
  sessionId?: string;
  classId?: string;
}

export const getTopOutstandingStudents = async (filter: TopOutstandingStudentsFilter = {}): Promise<TopOutstandingStudentsResponse> => {
  const sp = new URLSearchParams();
  if (filter.limit)     sp.append('limit', String(filter.limit));
  if (filter.sessionId) sp.append('sessionId', filter.sessionId);
  if (filter.classId)   sp.append('classId', filter.classId);
  const response = await api.get<ApiResponse<TopOutstandingStudentsResponse>>(`/finance-reports/fees/top-outstanding-students?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export interface PaymentMethodMixFilter {
  sessionId?: string;
  classId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export const getPaymentMethodMix = async (filter: PaymentMethodMixFilter = {}): Promise<PaymentMethodMixResponse> => {
  const sp = new URLSearchParams();
  if (filter.sessionId) sp.append('sessionId', filter.sessionId);
  if (filter.classId)   sp.append('classId', filter.classId);
  if (filter.dateFrom)  sp.append('dateFrom', filter.dateFrom);
  if (filter.dateTo)    sp.append('dateTo', filter.dateTo);
  const response = await api.get<ApiResponse<PaymentMethodMixResponse>>(`/finance-reports/fees/payment-methods?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export interface AdjustmentsSummaryFilter {
  sessionId?: string;
  classId?: string;
}

export const getAdjustmentsSummary = async (filter: AdjustmentsSummaryFilter = {}): Promise<AdjustmentsSummaryResponse> => {
  const sp = new URLSearchParams();
  if (filter.sessionId) sp.append('sessionId', filter.sessionId);
  if (filter.classId)   sp.append('classId', filter.classId);
  const response = await api.get<ApiResponse<AdjustmentsSummaryResponse>>(`/finance-reports/fees/adjustments-summary?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

const feeReportsService = {
  getAging,
  getCollectionRate,
  getByCategory,
  getDuesForecast,
  getCollectionTrend,
  getTopOutstandingStudents,
  getPaymentMethodMix,
  getAdjustmentsSummary,
};

export default feeReportsService;
